/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useProductsById} from './utils'

import {
    sendBeginCheckoutEvent,
    sendShippingAddressEvent,
    sendShippingInfoEvent,
    sendClickEvent
} from '../../../utils/gtm-utils'
import {sendBrazeBeginCheckoutEvent} from '../../../utils/braze-utils'

const CheckoutContext = React.createContext()

export const CheckoutProvider = ({children}) => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const einstein = useEinstein()
    const [step, setStep] = useState()
    const [ispayment, setIsPayment] = useState(false)

    const CHECKOUT_STEPS_LIST = [
        'CONTACT_INFO',
        'SHIPPING_ADDRESS',
        'SHIPPING_OPTIONS',
        'PAYMENT',
        'REVIEW_ORDER'
    ]
    const STEPS = CHECKOUT_STEPS_LIST.reduce((acc, step, idx) => ({...acc, [step]: idx}), {})

    const getCheckoutStepName = (step) => CHECKOUT_STEPS_LIST[step]

    useEffect(() => {
        if (!customer || !basket) {
            return
        }
        const currentShippingMethod = basket?.shipments?.[0]?.shippingMethod
        const isEGCShippingMethod = currentShippingMethod?.c_eGiftCardShipMethod || false
        let step = isEGCShippingMethod || ispayment ? STEPS.PAYMENT : STEPS.SHIPPING_OPTIONS
        if (customer.isGuest && !basket.customerInfo?.email) {
            step = STEPS.CONTACT_INFO
        } else if (
            !basket.shipments[0]?.shippingAddress ||
            basket.shipments[0]?.shippingAddress?.address1 === 'basketOnly'
        ) {
            step = STEPS.SHIPPING_ADDRESS
        } else if (!ispayment && !isEGCShippingMethod && (basket.shipments[0]?.shippingAddress || !basket.shipments[0]?.shippingMethod)) {
            step = STEPS.SHIPPING_OPTIONS
        } else {
            // Check if basket contains only E-Gift Cards but has wrong shipping method
            const hasOnlyEGiftCards = basket.productItems?.length > 0 && basket.productItems.every(item =>
                item.c_isEGiftCard === true
            )
            const currentShippingMethod = basket.shipments[0]?.shippingMethod

            if (hasOnlyEGiftCards && currentShippingMethod && currentShippingMethod.c_eGiftCardShipMethod !== true) {
                step = STEPS.SHIPPING_OPTIONS
            } else if (!basket.paymentInstruments || !basket.billingAddress) {
                step = STEPS.PAYMENT
            }
        }

        setStep(step)
    }, [
        customer?.isGuest,
        basket?.customerInfo?.email,
        basket?.shipments[0]?.shippingAddress,
        basket?.shipments[0]?.shippingMethod,
        basket?.paymentInstruments,
        basket?.billingAddress,
        basket?.productItems,
        ispayment
    ])

    const isBrazeEnabled = getConfig()?.app?.IS_BRAZE_ENABLED === 'true'
    const productIds = useMemo(() => {
        if (!isBrazeEnabled || !basket?.productItems?.length) {
            return ''
        }
        return basket.productItems.map(({ productId }) => productId).join(',')
    }, [isBrazeEnabled, basket?.productItems])

    const { data: products } = useProductsById(productIds, {
        enabled: isBrazeEnabled && Boolean(productIds)
    })
    const beginCheckoutSent = useRef(false)

    useEffect(() => {
        if (
            !isBrazeEnabled ||
            beginCheckoutSent.current ||
            !basket?.productItems?.length ||
            !products
        ) {
            return
        }

        // Braze Modification - Send Braze Begin Checkout Event
        sendBrazeBeginCheckoutEvent(basket, products)

        beginCheckoutSent.current = true
    }, [isBrazeEnabled, basket, products])

    /**************** Einstein ****************/
    // Run this once when checkout begins
    useEffect(() => {
        if (basket?.productItems) {
            einstein.sendBeginCheckout(basket)
            sendBeginCheckoutEvent(basket)
        }
    }, [])

    // Run this every time checkout steps change
    useEffect(() => {
        if (step != undefined) {
            einstein.sendCheckoutStep(getCheckoutStepName(step), step, basket)

            // GTM Modification
            if (step == 3) {
                sendShippingAddressEvent(basket)
                sendShippingInfoEvent(basket)

                if (basket && basket.shipments.length && basket.shipments[0].shippingMethod) {
                    switch (basket.shipments[0].shippingMethod.c_signifydShippingMethodType) {
                        case 'STANDARD':
                            sendClickEvent('shipping-methods_select_standard-ground')
                            break

                        case 'EXPRESS':
                            sendClickEvent('shipping-methods_select_express-shipping')
                            break

                        default:
                            sendClickEvent('shipping-methods_select_priority-shipping')
                    }
                }
            }
        }
    }, [step])

    const value = {
        step,
        STEPS,
        goToNextStep: () => setStep(step + 1),
        goToStep: (step) => setStep(step),
        ispayment,
        setIsPayment
    }

    return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>
}

CheckoutProvider.propTypes = {
    children: PropTypes.any
}

/**
 * A hook for managing checkout state and actions
 * @returns {Object} Checkout data and actions
 */
export const useCheckout = () => {
    return React.useContext(CheckoutContext)
}
